import React, { useContext, useEffect, useMemo, useState } from 'react';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { AuthContext } from './../AuthContext';
import api from './../api.js';
import { isStaffUser } from './../role.js';
import { IndianCurrencyFormatter } from './../Function';
import { useLocation, useNavigate } from 'react-router-dom';

function monthStart() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${m}-01`;
}

function today() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function Index() {
  const { user, fulluser } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const staffLogin = isStaffUser(fulluser);
  const queryId = useMemo(() => new URLSearchParams(location.search).get('id') || '', [location.search]);

  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState(queryId);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [summary, setSummary] = useState(null);
  const [bills, setBills] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const backdrops = document.querySelectorAll('.modal-backdrop');
    backdrops.forEach((el) => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }, [location]);

  useEffect(() => {
    if (!user) return;
    if (!staffLogin) {
      api.get('/vendor/staff').then((res) => {
        if (res.data.status === 'success') {
          setStaffList(res.data.staff || []);
        }
      }).catch(() => {});
    }
  }, [user, staffLogin]);

  useEffect(() => {
    if (queryId) setStaffId(queryId);
  }, [queryId]);

  const loadReport = async () => {
    if (!staffLogin && !staffId) {
      setSummary(null);
      setBills([]);
      return;
    }
    setLoading(true);
    try {
      const url = staffLogin
        ? `/staff/report?from=${from}&to=${to}`
        : `/vendor/staff/report?staff_id=${staffId}&from=${from}&to=${to}`;
      const response = await api.get(url);
      if (response.data.status === 'success') {
        setSummary(response.data.summary || null);
        setBills(response.data.bills || []);
        setSelectedStaff(response.data.staff || null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) loadReport();
  }, [user, staffId, from, to, staffLogin]);

  const cards = [
    { label: 'Sales', value: summary?.sales || 0 },
    { label: 'Collection', value: summary?.collection || 0 },
    { label: 'Cash', value: summary?.cash || 0 },
    { label: 'Online', value: summary?.online || 0 },
    { label: 'Reward used', value: summary?.reward_used || 0 },
  ];

  return (
    <div id="appCapsule">
      <Footer />
      <Sidebar />
      <div className="section mt-2">
        <div className="section-heading">
          <h2 className="title">{staffLogin ? 'My Report' : 'Staff Report'}</h2>
        </div>
        <div className="card mb-2">
          <div className="card-body">
            {!staffLogin && (
              <div className="form-group basic">
                <label className="label">Staff</label>
                <select
                  className="form-control"
                  value={staffId}
                  onChange={(e) => {
                    setStaffId(e.target.value);
                    navigate(e.target.value ? `/StaffReport?id=${e.target.value}` : '/StaffReport');
                  }}
                >
                  <option value="">Select staff</option>
                  {staffList.map((item) => (
                    <option key={item.id} value={item.id}>{item.name} - {item.phone}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="row">
              <div className="col-6">
                <label className="label">From</label>
                <input type="date" className="form-control" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="col-6">
                <label className="label">To</label>
                <input type="date" className="form-control" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {selectedStaff && (
          <div className="card mb-2">
            <div className="card-body">
              <strong>{selectedStaff.name}</strong>
              <div className="text-muted">{selectedStaff.phone}</div>
              <span className={`badge ${Number(selectedStaff.status) === 0 ? 'badge-success' : 'badge-danger'}`}>
                {Number(selectedStaff.status) === 0 ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center p-3">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : summary ? (
          <div className="row">
            {cards.map((card) => (
              <div className="col-6 mb-2" key={card.label}>
                <div className="stat-box">
                  <div className="title">{card.label}</div>
                  <div className="value text-primary">
                    <IndianCurrencyFormatter amount={card.value} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card">
            <div className="card-body text-center text-muted">
              {staffLogin ? 'No report data' : 'Select a staff member to view report'}
            </div>
          </div>
        )}
      </div>

      <div className="section mb-5">
        <div className="section-heading">
          <h2 className="title">Bill details</h2>
        </div>
        {bills.length === 0 ? (
          <div className="card">
            <div className="card-body text-center text-muted">No bills in this date range.</div>
          </div>
        ) : (
          <ul className="listview image-listview">
            {bills.map((item) => (
              <li key={item.id}>
                <div className="item">
                  <div className="in">
                    <div>
                      <strong>#{item.billno}</strong>
                      <div className="text-muted small">{item.date_time}</div>
                      <div className="small">{item.customer_name || 'Customer'} {item.customer_phone ? `- ${item.customer_phone}` : ''}</div>
                      <div className="small">
                        {item.paytype} | Reward <IndianCurrencyFormatter amount={item.customer_reward_used || 0} />
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="price text-danger">
                        <IndianCurrencyFormatter amount={(parseFloat(item.billamount || 0) + parseFloat(item.tax || 0))} />
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default Index;
